import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { ListChecks, Eye, Clock, Plus, Search, MousePointerClick } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, type SelectOption } from "@/components/ui/select"
import { CustomFieldRow, CUSTOM_FIELD_GRID_COLS } from "@/components/domain/custom-field-row"
import { CustomFieldDrawer } from "@/components/domain/custom-field-drawer"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type {
  CustomFieldsIndexProps,
  CustomFieldParentType,
} from "@/types/custom_field"

// Custom Fields curation page. One row per CustomFieldDefinition (the
// per-company catalog of typed, semantic, named fields) — pending rows
// surface auto-created keys awaiting operator review; live rows feed
// the LLM prompt + routing predicates; archived rows are read-only.
//
// Tabs split by parent_type (Conversations vs App Users) because the
// `(company_id, parent_type, key)` uniqueness constraint means the
// same key string can carry different definitions on each side.

const BASE = "/custom_fields"

const TYPE_FILTER_OPTIONS: SelectOption<string>[] = [
  { value: "",             label: "All types" },
  { value: "text",         label: "Text" },
  { value: "number",       label: "Number" },
  { value: "boolean",      label: "Checkbox" },
  { value: "date",         label: "Date" },
  { value: "select",       label: "Dropdown" },
  { value: "multi_select", label: "Multi-select" },
]

const STATUS_FILTER_OPTIONS: SelectOption<string>[] = [
  { value: "",         label: "All statuses" },
  { value: "pending",  label: "Pending" },
  { value: "live",     label: "Active" },
  { value: "archived", label: "Archived" },
]

function ColumnHeaders() {
  return (
    <div
      className={cn(
        CUSTOM_FIELD_GRID_COLS,
        "border-b border-border bg-app-panel2/30 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-app-muted",
      )}
    >
      <div>Field name</div>
      <div className="hidden md:block">Key</div>
      <div className="hidden md:block">Type</div>
      <div className="hidden md:block">Status</div>
    </div>
  )
}

export default function CustomFieldsIndex({
  parentType,
  counts,
  pending,
  live,
  archived,
  filters,
  lastUpdatedAt,
  lastUpdatedRow,
}: CustomFieldsIndexProps) {
  // selectedId: number = existing row, "new" = create mode, null = closed.
  const [selectedId, setSelectedId] = useState<number | "new" | null>(null)

  // Search input is debounced — pushing to the URL on every keystroke
  // would re-fetch the page payload server-side per character.
  const [q, setQ] = useState(filters.q ?? "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (q === (filters.q ?? "")) return
      pushFilters({ q })
    }, 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const pushFilters = (next: Partial<{ q: string; type: string; status: string; parent_type: CustomFieldParentType }>) => {
    router.get(
      BASE,
      {
        parent_type: next.parent_type ?? parentType,
        q:           next.q ?? filters.q ?? undefined,
        type:        next.type ?? filters.type ?? undefined,
        status:      next.status ?? filters.status ?? undefined,
      },
      { preserveScroll: true, preserveState: true, replace: true },
    )
  }

  const switchParentType = (pt: CustomFieldParentType) => {
    setSelectedId(null)
    router.get(BASE, { parent_type: pt }, { preserveScroll: true, preserveState: false })
  }

  const allRows = useMemo(
    () => [...pending, ...live, ...archived],
    [pending, live, archived],
  )

  const selectedDef = useMemo(
    () => (typeof selectedId === "number" ? allRows.find((r) => r.id === selectedId) ?? null : null),
    [selectedId, allRows],
  )
  const isCreating = selectedId === "new"

  // Drop the selection if a refresh wiped the row.
  useEffect(() => {
    if (typeof selectedId === "number" && !allRows.some((r) => r.id === selectedId)) setSelectedId(null)
  }, [allRows, selectedId])

  const totalForTab    = counts[parentType].pending + counts[parentType].live + counts[parentType].archived
  const liveCount      = counts[parentType].live
  const pendingCount   = counts[parentType].pending
  // Render order — pending first (curation queue), then live (sorted by
  // position via presenter), then archived. Operators expect to see
  // pending at the top so newly-pushed keys don't get buried.
  const orderedRows = useMemo(() => {
    if (filters.status === "pending")  return pending
    if (filters.status === "live")     return live
    if (filters.status === "archived") return archived
    return [...pending, ...live, ...archived]
  }, [filters.status, pending, live, archived])

  return (
    <AppShell>
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-1.5 text-app-muted">
            <span>Settings</span>
            <span className="text-app-subtle">/</span>
            <span className="text-app-text">Custom Fields</span>
          </span>
        }
        title="Custom Fields"
        subtitle="Create and manage custom fields to capture the information that matters to your team."
        actions={
          <Button onClick={() => setSelectedId("new")} className="w-full sm:w-auto">
            <Plus size={16} className="mr-1.5" />
            Create field
          </Button>
        }
      />

      {/* Tabs — underlined, red on active. */}
      <div className="flex items-center gap-1 border-b border-border">
        <Tab
          active={parentType === "agent_convo"}
          onClick={() => switchParentType("agent_convo")}
          badge={counts.agent_convo.pending > 0 ? counts.agent_convo.pending : null}
        >
          Conversations
        </Tab>
        <Tab
          active={parentType === "appuser"}
          onClick={() => switchParentType("appuser")}
          badge={counts.appuser.pending > 0 ? counts.appuser.pending : null}
        >
          App Users
        </Tab>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Total fields"
          value={totalForTab}
          sub="Across all types"
          icon={<ListChecks size={18} />}
          tint="violet"
        />
        <StatCard
          label="In use"
          value={liveCount}
          sub="Visible in forms"
          icon={<Eye size={18} />}
          tint="blue"
        />
        <StatCard
          label="Last updated"
          value={lastUpdatedAt ? fmtCompactRelative(lastUpdatedAt) : "—"}
          // Field name, not user attribution. We don't track
          // updated_by_user_id today, so the most useful "what
          // changed" signal is which definition was touched last.
          sub={lastUpdatedRow ? lastUpdatedRow : "No edits yet"}
          icon={<Clock size={18} />}
          tint="amber"
        />
      </div>

      {/* Two-column shell — drawer column is ALWAYS reserved so the page
          doesn't reflow when a row opens. Empty placeholder shown when
          nothing is selected. Table takes ~2/3, drawer ~1/3 via
          standard Tailwind 12-col split. */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
        <Card className="overflow-hidden p-0">
          {/* Filter row */}
          <div className="grid grid-cols-1 gap-3 border-b border-border px-4 py-3 sm:grid-cols-[1fr_180px_180px]">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-muted" />
              <Input
                type="text"
                placeholder="Search fields by name or key…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-9"
              />
            </div>
            <Select<string>
              value={TYPE_FILTER_OPTIONS.find((o) => o.value === (filters.type ?? "")) ?? TYPE_FILTER_OPTIONS[0]}
              onChange={(o) => o && pushFilters({ type: o.value })}
              options={TYPE_FILTER_OPTIONS}
              isSearchable={false}
            />
            <Select<string>
              value={STATUS_FILTER_OPTIONS.find((o) => o.value === (filters.status ?? "")) ?? STATUS_FILTER_OPTIONS[0]}
              onChange={(o) => o && pushFilters({ status: o.value })}
              options={STATUS_FILTER_OPTIONS}
              isSearchable={false}
            />
          </div>

          {orderedRows.length === 0 ? (
            <EmptyState parentType={parentType} onCreate={() => setSelectedId("new")} />
          ) : (
            <>
              <div className="hidden md:block">
                <ColumnHeaders />
              </div>
              <div>
                {orderedRows.map((d) => (
                  <CustomFieldRow
                    key={d.id}
                    definition={d}
                    selected={d.id === selectedId}
                    onClick={() => setSelectedId(d.id)}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-app-muted">
                <span>Showing {orderedRows.length} of {totalForTab} fields</span>
                {pendingCount > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-status-warning" />
                    {pendingCount} pending review
                  </span>
                )}
              </div>
            </>
          )}
        </Card>
        </div>

        {/* Drawer column — always rendered. Shows the editor when a row
            is selected (or in create mode), otherwise a placeholder so
            the page layout stays stable. Sticky positioning only kicks
            in at lg:+ where the 2-col grid is active; on mobile the
            drawer flows in-line below the table and uses its natural
            height. */}
        <div className="lg:col-span-1 min-w-0">
        <Card className="overflow-hidden p-0 lg:sticky lg:top-2 lg:self-start lg:h-[calc(100vh-2rem)]">
          {selectedDef || isCreating ? (
            <CustomFieldDrawer
              key={selectedId === "new" ? "new" : selectedDef!.id}
              mode={isCreating ? "create" : "edit"}
              definition={selectedDef ?? undefined}
              parentType={parentType}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <DrawerEmptyState onCreate={() => setSelectedId("new")} />
          )}
        </Card>
        </div>
      </div>
    </AppShell>
  )
}

function DrawerEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-card bg-white/5 text-app-muted">
        <MousePointerClick size={20} />
      </div>
      <div className="text-sm font-medium text-app-text">Select a custom field</div>
      <p className="max-w-xs text-xs text-app-muted">
        Click any row on the left to view or edit its configuration. Or create a new field from scratch.
      </p>
      <Button size="sm" onClick={onCreate}>
        <Plus size={14} className="mr-1.5" />
        Create field
      </Button>
    </div>
  )
}

function Tab({
  active,
  onClick,
  badge,
  children,
}: {
  active: boolean
  onClick: () => void
  badge?: number | null
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative -mb-px border-b-2 px-4 py-2.5 text-sm transition",
        active
          ? "border-brand-red text-brand-red font-semibold"
          : "border-transparent text-app-muted hover:text-app-text",
      )}
    >
      {children}
      {badge != null && badge > 0 && (
        <span className="ml-2 inline-flex min-w-[18px] items-center justify-center rounded-chip bg-status-warning/20 px-1.5 py-0.5 text-[10px] font-semibold text-status-warning">
          {badge}
        </span>
      )}
    </button>
  )
}

const STAT_TINT: Record<string, { bg: string; fg: string }> = {
  violet: { bg: "bg-violet-500/15",         fg: "text-violet-300" },
  blue:   { bg: "bg-sky-500/15",            fg: "text-sky-300" },
  green:  { bg: "bg-status-success/15",     fg: "text-status-success" },
  amber:  { bg: "bg-status-warning/15",     fg: "text-status-warning" },
}

function StatCard({
  label,
  value,
  sub,
  icon,
  tint,
}: {
  label: string
  value: number | string
  sub?: string
  icon: React.ReactNode
  tint: keyof typeof STAT_TINT
}) {
  const tones = STAT_TINT[tint]
  return (
    <Card className="flex items-center gap-3 px-4 py-3.5">
      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-control", tones.bg, tones.fg)}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-app-muted">{label}</div>
        <div className="mt-0.5 text-xl font-semibold text-app-text">{value}</div>
        {sub && <div className="truncate text-[11px] text-app-muted">{sub}</div>}
      </div>
    </Card>
  )
}

function EmptyState({
  parentType,
  onCreate,
}: {
  parentType: CustomFieldParentType
  onCreate: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-card bg-white/5 text-app-muted">
        <ListChecks size={20} />
      </div>
      <div className="text-sm font-medium text-app-text">
        No custom fields for {parentType === "agent_convo" ? "Conversations" : "App Users"} yet
      </div>
      <p className="max-w-md text-xs text-app-muted">
        Create one to capture structured data, or push values via the API and they'll auto-appear here as pending fields awaiting review.
      </p>
      <Button size="sm" onClick={onCreate}>
        <Plus size={14} className="mr-1.5" />
        Create field
      </Button>
    </div>
  )
}
